import { ClosingRule, WorkshopV2 } from '../interfaces/workshop.interface';

/**
 * Fermeture automatique des réservations.
 *
 * La règle choisie par l'admin (`closingRule`) sert uniquement à réafficher la
 * config dans le formulaire. Ce qui fait foi partout ailleurs — dashboard et
 * site vitrine — c'est `bookingClosesAt`, un instant absolu recalculé à chaque
 * sauvegarde. Les consommateurs n'ont donc qu'une comparaison à faire, et
 * aucune logique de fuseau horaire à dupliquer.
 */

const TZ = 'Europe/Paris';

/** Heure de fermeture pour la règle « la veille à 18h ». */
export const DAY_BEFORE_HOUR = 18;

/** Date civile à Paris (le locale sv-SE produit de l'ISO). */
const parisParts = (date: Date): { y: number; m: number; d: number } => {
  const iso = new Intl.DateTimeFormat('sv-SE', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);

  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d };
};

/** Décalage de Paris par rapport à UTC à cet instant précis (+1h ou +2h selon la saison). */
const parisOffsetMs = (date: Date): number => {
  const name = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    timeZoneName: 'longOffset',
  })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value;

  const match = name?.match(/GMT([+-])(\d{2}):(\d{2})/);
  if (!match) return 0;

  const [, sign, hours, minutes] = match;
  const ms = (Number(hours) * 60 + Number(minutes)) * 60_000;
  return sign === '-' ? -ms : ms;
};

/**
 * Instant UTC correspondant à `hour`h00 heure de Paris, `plusDays` jours après
 * le jour civil parisien de `base`. On calcule en heure de Paris et non en heure
 * locale du navigateur : « 18h la veille » doit valoir 18h à l'atelier, que
 * l'admin saisisse depuis Les Sables d'Olonne ou depuis ailleurs.
 */
const parisInstantOnDay = (base: Date, plusDays: number, hour: number): Date => {
  const { y, m, d } = parisParts(base);
  const naive = Date.UTC(y, m - 1, d + plusDays, hour);

  // Le décalage se déduit de l'instant lui-même : on l'estime, puis on le
  // recalcule sur la date corrigée (le changement d'heure a lieu à 2h du matin,
  // jamais à 18h — une seule passe suffit).
  const offset = parisOffsetMs(new Date(naive - parisOffsetMs(new Date(naive))));
  return new Date(naive - offset);
};

/**
 * Instant de fermeture des réservations, ou `null` si l'atelier reste ouvert
 * jusqu'au bout. À rappeler à chaque sauvegarde : si la date de l'atelier
 * change, la fermeture « la veille à 18h » doit suivre.
 */
export const computeBookingClosesAt = (
  rule: ClosingRule,
  workshopDate: Date | null,
  customDate: Date | null
): Date | null => {
  switch (rule) {
    case 'DAY_BEFORE_18H':
      if (!workshopDate || Number.isNaN(workshopDate.getTime())) return null;
      return parisInstantOnDay(workshopDate, -1, DAY_BEFORE_HOUR);

    case 'CUSTOM':
      if (!customDate || Number.isNaN(customDate.getTime())) return null;
      return customDate;

    default:
      return null;
  }
};

/**
 * Vrai si les réservations sont closes. Les ateliers créés avant cette
 * fonctionnalité n'ont pas de `bookingClosesAt` : ils restent ouverts.
 */
export const isBookingClosed = (
  workshop: Pick<WorkshopV2, 'bookingClosesAt'>,
  now: Date = new Date()
): boolean => {
  const closesAt = workshop.bookingClosesAt;
  if (!closesAt) return false;
  return closesAt.toMillis() <= now.getTime();
};

/** « lundi 20 juillet à 18:00 » — pour afficher l'échéance à l'admin. */
export const formatClosingDate = (closesAt: Date): string =>
  closesAt.toLocaleString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  });

/** Libellés français des règles, pour les <select> du formulaire. */
export const CLOSING_RULE_LABELS: Record<ClosingRule, string> = {
  NONE: "Jusqu'au début de l'atelier",
  DAY_BEFORE_18H: 'La veille à 18h',
  CUSTOM: 'À une date précise',
};
